import { createContext, useContext, useMemo, useState, useCallback, useEffect } from "react";
import { logger } from "../utils/logger";
import { recordParcelEngagement } from "../utils/parcelEngagement";

const ParcelContext = createContext();

const getParcelKey = (parcel) => {
  if (!parcel) return undefined;
  return parcel.id ?? `${parcel.APN ?? ""}-${parcel.Zip5 ?? parcel.Zip ?? ""}-${parcel.Address ?? ""}`;
};

export const ParcelProvider = ({ children }) => {
  const [allParcels, setAllParcels] = useState([]);
  const [selectedParcels, setSelectedParcels] = useState([]);

  const noteParcelEngagement = useCallback((parcelOrId, event) => {
    recordParcelEngagement(parcelOrId, event);
  }, []);
  
  // Caching state for efficient parcel management
  const [cachedParcels, setCachedParcels] = useState([]);
  const [cachedBounds, setCachedBounds] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(null);

  // Persistent state for map view navigation
  const [mapViewState, setMapViewState] = useState({
    // Map state
    mapCenter: null,
    mapZoom: null,
    mapBounds: null,
    
    // Parcel display state
    parcelData: [],
    mapParcelData: [],
    
    // Search and filter state
    searchFilters: {},
    currentPage: 1,
    hasNextPage: false,
    totalFilteredCount: 0,
    
    // Loading states
    parcelLoading: false,
    isLoadingMore: false,
    isFiltering: false,
    
    // UI interaction state
    highlightedParcelId: null,
  });

  const isParcelSelected = useCallback(
    (parcel) => {
      const key = getParcelKey(parcel);
      return selectedParcels.some((p) => getParcelKey(p) === key);
    },
    [selectedParcels]
  );

  const toggleParcelSelection = useCallback((parcel) => {
    const key = getParcelKey(parcel);
    if (key === undefined) return;
    setSelectedParcels((prev) => {
      const exists = prev.some((p) => getParcelKey(p) === key);
      return exists ? prev.filter((p) => getParcelKey(p) !== key) : [...prev, parcel];
    });
  }, []);

  const clearSelectedParcels = useCallback(() => setSelectedParcels([]), []);

  // Debug logging for cache changes
  useEffect(() => {
    if (cachedParcels.length > 0) {
      logger.log(`Cache state updated: ${cachedParcels.length} parcels cached`);
      logger.log(`First cached parcel:`, cachedParcels[0]);
    }
  }, [cachedParcels]);

  // Check if current bounds require a cache refetch based on coverage
  const needsRefetch = useCallback((currentBounds) => {
    if (!cachedBounds || !currentBounds) return true;
    
    // Calculate the intersection of current bounds with cached bounds
    const interLat = Math.max(0, Math.min(cachedBounds.north, currentBounds.north) - Math.max(cachedBounds.south, currentBounds.south));
    const interLng = Math.max(0, Math.min(cachedBounds.east, currentBounds.east) - Math.max(cachedBounds.west, currentBounds.west));
    const interArea = interLat * interLng;
    
    // Calculate the area of the current bounds
    const currentArea = Math.max(0, (currentBounds.north - currentBounds.south)) * Math.max(0, (currentBounds.east - currentBounds.west));
    
    // Calculate how much of the current view is covered by the cache
    const coverage = currentArea > 0 ? interArea / currentArea : 0;
    
    // Refetch when less than 20% of current view is covered by cache
    const shouldRefetch = coverage < 0.2;
    
    logger.log(`Cache coverage analysis:`, {
      cachedBounds: `${cachedBounds.south.toFixed(4)},${cachedBounds.west.toFixed(4)},${cachedBounds.north.toFixed(4)},${cachedBounds.east.toFixed(4)}`,
      currentBounds: `${currentBounds.south.toFixed(4)},${currentBounds.west.toFixed(4)},${currentBounds.north.toFixed(4)},${currentBounds.east.toFixed(4)}`,
      intersectionArea: interArea.toFixed(6),
      currentViewArea: currentArea.toFixed(6),
      coveragePercent: `${(coverage * 100).toFixed(1)}%`,
      shouldRefetch: shouldRefetch,
      reason: shouldRefetch ? 'Cache coverage < 20%' : 'Sufficient cache coverage'
    });
    
    return shouldRefetch;
  }, [cachedBounds]);

  // Filter cached parcels to current viewport
  const filterParcelsToViewport = useCallback((bounds, parcelsToFilter = null) => {
    const parcels = parcelsToFilter || cachedParcels;
    logger.log(`Filtering parcels to viewport: ${parcels.length}`);
    if (!bounds || parcels.length === 0) return [];
    
    const filtered = parcels.filter(parcel => {
      const lat = parseFloat(parcel.Center_Lat);
      const lng = parseFloat(parcel.Center_Lon);
      
      if (isNaN(lat) || isNaN(lng)) return false;
      
      return lat >= bounds.south && lat <= bounds.north && 
             lng >= bounds.west && lng <= bounds.east;
    });
    
    logger.log(`After viewport filtering: ${filtered.length} parcels`);
    return filtered;
  }, [cachedParcels]);

  // Update cache with new parcel data
  const updateParcelCache = useCallback((newParcels, bounds) => {
    logger.log(`Updating parcel cache with ${newParcels.length} parcels`);
    setCachedParcels(newParcels);
    setCachedBounds(bounds);
    setLastFetchTime(Date.now());
    logger.log(`Cache updated - bounds: ${bounds.south},${bounds.west},${bounds.north},${bounds.east}`);
  }, []);

  // Map view state management functions
  const updateMapViewState = useCallback((updates) => {
    setMapViewState(prev => ({ ...prev, ...updates }));
  }, []);

  const updateMapPosition = useCallback((center, zoom, bounds) => {
    setMapViewState(prev => ({
      ...prev,
      mapCenter: center,
      mapZoom: zoom,
      mapBounds: bounds,
    }));
  }, []);

  const updateParcelDisplay = useCallback((parcelData, mapParcelData, pagination = {}) => {
    setMapViewState(prev => ({
      ...prev,
      parcelData,
      mapParcelData,
      ...pagination
    }));
  }, []);

  const updateSearchState = useCallback((searchFilters, resetPagination = false) => {
    setMapViewState(prev => ({
      ...prev,
      searchFilters,
      ...(resetPagination && {
        currentPage: 1,
        hasNextPage: false,
        totalFilteredCount: 0
      })
    }));
  }, []);

  const updateHighlightedParcel = useCallback((parcelId) => {
    setMapViewState(prev => ({
      ...prev,
      highlightedParcelId: parcelId
    }));
    noteParcelEngagement(parcelId, "inspected");
  }, [noteParcelEngagement]);

  const resetMapViewState = useCallback(() => {
    setMapViewState({
      mapCenter: null,
      mapZoom: null,
      mapBounds: null,
      parcelData: [],
      mapParcelData: [],
      searchFilters: {},
      currentPage: 1,
      hasNextPage: false,
      totalFilteredCount: 0,
      parcelLoading: false,
      isLoadingMore: false,
      isFiltering: false,
      highlightedParcelId: null,
    });
  }, []);

  const value = useMemo(
    () => ({
      allParcels,
      setAllParcels,
      selectedParcels,
      setSelectedParcels,
      isParcelSelected,
      toggleParcelSelection,
      clearSelectedParcels,
      // Cache management
      cachedParcels,
      cachedBounds,
      lastFetchTime,
      needsRefetch,
      filterParcelsToViewport,
      updateParcelCache,
      // Map view state management
      mapViewState,
      updateMapViewState,
      updateMapPosition,
      updateParcelDisplay,
      updateSearchState,
      updateHighlightedParcel,
      noteParcelEngagement,
      resetMapViewState,
    }),
    [
      allParcels, 
      selectedParcels, 
      isParcelSelected, 
      toggleParcelSelection,
      clearSelectedParcels,
      cachedParcels,
      cachedBounds,
      lastFetchTime,
      needsRefetch,
      filterParcelsToViewport,
      updateParcelCache,
      mapViewState,
      updateMapViewState,
      updateMapPosition,
      updateParcelDisplay,
      updateSearchState,
      updateHighlightedParcel,
      noteParcelEngagement,
      resetMapViewState,
    ]
  );

  return (
    <ParcelContext.Provider value={value}>
      {children}
    </ParcelContext.Provider>
  );
};

export const useParcels = () => useContext(ParcelContext);
